import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { ShieldCheck, Trash2, CheckCircle2, AlertCircle } from "lucide-react";

function formatTime(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return ""; }
}

export default function Admin() {
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("open");
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    try {
      const [r, u] = await Promise.all([
        api.get(`/admin/reports?status_filter=${filter}`),
        api.get("/admin/users"),
      ]);
      setReports(r.data || []);
      setUsers(u.data || []);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const onResolve = async (id) => {
    try {
      await api.post(`/admin/reports/${id}/resolve`);
      load();
    } catch (e) {
      window.alert(formatApiError(e));
    }
  };

  const onDeleteMsg = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/messages/${id}`);
      load();
    } catch (e) {
      window.alert(formatApiError(e));
    }
  };

  return (
    <div data-testid="page-admin" className="py-16 lg:py-20">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-moss mb-6">
          <span className="w-6 h-px bg-moss" />
          Admin dashboard
        </span>
        <h1 className="font-serif text-charcoal dark:text-bone text-4xl sm:text-5xl tracking-tight">
          <ShieldCheck size={32} strokeWidth={1.5} className="inline-block text-terracotta mr-3" />
          Quiet moderation.
        </h1>

        {err && <p className="mt-6 text-sm text-terracotta">{err}</p>}

        {/* Reports */}
        <section className="mt-12" data-testid="admin-reports">
          <div className="flex items-end justify-between mb-5">
            <h2 className="font-serif text-2xl text-charcoal dark:text-bone tracking-tight">Reports</h2>
            <div className="flex gap-2">
              {["open", "resolved", "all"].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  data-testid={`filter-${f}`}
                  className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.22em] transition ${
                    filter === f
                      ? "bg-terracotta text-bone"
                      : "bg-bone-100 dark:bg-charcoal-light/15 text-charcoal-light dark:text-bone/65 hover:text-charcoal dark:hover:text-bone"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {reports.length === 0 ? (
            <div data-testid="reports-empty" className="rounded-3xl border border-bone-200 dark:border-charcoal-light/25 bg-bone dark:bg-charcoal-light/10 p-8 text-sm text-charcoal-light dark:text-bone/60">
              No reports{filter !== "all" ? ` with status "${filter}"` : ""} right now.
            </div>
          ) : (
            <div className="space-y-4">
              {reports.map((r) => (
                <div
                  key={r.id}
                  data-testid={`report-row-${r.id}`}
                  className="rounded-3xl border border-bone-200 dark:border-charcoal-light/25 bg-bone dark:bg-charcoal-light/10 p-6"
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <AlertCircle size={18} strokeWidth={1.5} className="text-terracotta" />
                      <span className={`text-[0.65rem] uppercase tracking-[0.22em] ${r.status === "open" ? "text-terracotta" : "text-moss"}`}>
                        {r.status}
                      </span>
                      <span className="text-xs text-charcoal-light dark:text-bone/55">{formatTime(r.created_at)}</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => onDeleteMsg(r.message_id)}
                        data-testid={`admin-delete-${r.message_id}`}
                        className="inline-flex items-center gap-1.5 text-xs text-charcoal-light dark:text-bone/65 hover:text-terracotta transition"
                      >
                        <Trash2 size={13} strokeWidth={1.5} />
                        Delete message
                      </button>
                      {r.status === "open" && (
                        <button
                          type="button"
                          onClick={() => onResolve(r.id)}
                          data-testid={`admin-resolve-${r.id}`}
                          className="inline-flex items-center gap-1.5 text-xs text-moss hover:text-terracotta transition"
                        >
                          <CheckCircle2 size={13} strokeWidth={1.5} />
                          Mark resolved
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="rounded-2xl bg-bone-100/60 dark:bg-charcoal-light/20 p-4 mb-3">
                    <p className="text-xs uppercase tracking-[0.22em] text-charcoal-light dark:text-bone/55 mb-1.5">
                      Message · {r.message_snapshot?.first_name || "Friend"} · #{r.message_snapshot?.channel_slug}
                    </p>
                    <p className="text-sm text-charcoal dark:text-bone whitespace-pre-wrap">
                      {r.message_snapshot?.content || "(unavailable)"}
                    </p>
                  </div>
                  {r.reason && (
                    <p className="text-sm text-charcoal-light dark:text-bone/65 italic">
                      Reason: {r.reason}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Users */}
        <section className="mt-16" data-testid="admin-users">
          <h2 className="font-serif text-2xl text-charcoal dark:text-bone tracking-tight mb-5">Members ({users.length})</h2>
          <div className="rounded-3xl border border-bone-200 dark:border-charcoal-light/25 bg-bone dark:bg-charcoal-light/10 overflow-hidden">
            <ul className="divide-y divide-bone-200 dark:divide-charcoal-light/20">
              {users.map((u) => (
                <li key={u.id} className="flex items-center justify-between gap-4 px-5 py-3 text-sm">
                  <div>
                    <span className="text-charcoal dark:text-bone font-medium">{u.first_name}</span>
                    <span className="ml-3 text-charcoal-light dark:text-bone/55">{u.email}</span>
                  </div>
                  <span className={`text-[0.65rem] uppercase tracking-[0.22em] ${u.role === "admin" ? "text-terracotta" : "text-charcoal-light dark:text-bone/55"}`}>
                    {u.role}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
